import React from 'react';
import './LegalPages.css';

const Privacy: React.FC = () => {
  return (
    <div className="legal-page">
      <div className="container">
        <header className="page-header">
          <h1>🔒 Privacy Policy</h1>
          <p className="date">Last updated: June 13, 2026</p>
        </header>

        <div className="legal-content">
          <p className="intro">
            Welcome to <strong>Flow XP</strong>, the mobile application dedicated to urban sports (breaking, freestyle football,
            street workout), community, challenges, and connected lifestyle. This privacy policy explains how we collect, use,
            and protect our users' personal data.
          </p>

          <section className="legal-section">
            <h2>1. Data Collected</h2>
            <h3>Account Data:</h3>
            <ul>
              <li>Email, username, profile picture</li>
              <li>Firebase unique identifier</li>
            </ul>

            <h3>Content Data:</h3>
            <ul>
              <li>Uploaded training videos</li>
              <li>Messages and conversations</li>
              <li>Shared photos and media</li>
              <li>Comments and social interactions</li>
            </ul>

            <h3>Progress Data:</h3>
            <ul>
              <li>Level, chosen class (Powerflow/Overflow/Masterflow)</li>
              <li>Flow points and credits</li>
              <li>Virtual item inventory</li>
              <li>Challenge and evaluation history</li>
              <li>Streaks and statistics</li>
            </ul>

            <h3>Technical Data:</h3>
            <ul>
              <li>Device identifier</li>
              <li>Push notification tokens</li>
              <li>App usage logs</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>2. How We Use Your Data</h2>
            <p>Your data is used to:</p>
            <ul>
              <li>Provide and improve app features</li>
              <li>Manage your account and progress</li>
              <li>Enable social interactions (chat, evaluations, comments)</li>
              <li>Send push notifications (can be disabled in settings)</li>
              <li>Ensure security and prevent abuse</li>
              <li>Moderate submitted content</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>3. Data Sharing</h2>
            <p>Your data may be shared with:</p>
            <ul>
              <li><strong>Firebase (Google)</strong>: hosting, authentication, storage, notifications</li>
              <li><strong>Google AdMob</strong>: ad display (see section on Advertising)</li>
              <li><strong>Singular</strong>: install attribution and app performance measurement (aggregated and anonymized data)</li>
              <li><strong>Other users</strong>: according to your interactions (public profile, messages, videos)</li>
            </ul>
            <p className="highlight-text">⚠️ We NEVER sell your personal data to third parties.</p>
          </section>

          <section className="legal-section">
            <h2>4. Your Rights (GDPR)</h2>
            <p>In accordance with the General Data Protection Regulation (GDPR), you have the following rights:</p>
            <ul>
              <li>Right of access to your data</li>
              <li>Right of rectification</li>
              <li>Right to erasure (right to be forgotten)</li>
              <li>Right to restriction of processing</li>
              <li>Right to data portability</li>
              <li>Right to object</li>
            </ul>
            <p>To exercise these rights, contact us at: <a href="mailto:flowledgeteam@gmail.com">flowledgeteam@gmail.com</a></p>
          </section>

          <section className="legal-section">
            <h2>5. Data Retention</h2>
            <p>
              Your data is retained as long as your account is active. After account deletion, your data is erased within
              <strong> 30 days</strong> (except legal retention obligations).
            </p>
            <p>
              <a href="/data-deletion">Learn more about account deletion</a>
            </p>
          </section>

          <section className="legal-section">
            <h2>6. Storage and Security</h2>
            <p>
              Data is stored securely on GDPR-compliant servers. Technical and organizational measures protect your data
              against unauthorized access, loss, or alteration.
            </p>
          </section>

          <section className="legal-section">
            <h2>7. Advertising and Tracking</h2>
            <p>
              The app displays ads via Google AdMob to fund the free service. AdMob and Singular may collect technical data
              (advertising identifier, device type) to personalize ads and measure app performance.
            </p>
            <p><strong>On iOS:</strong> Flow XP requests permission via App Tracking Transparency (ATT) before accessing
              your advertising identifier. You can decline when prompted or change this later in
              Settings → Privacy → Tracking.</p>
            <p>You can also disable personalized ads in your device settings:</p>
            <ul>
              <li><strong>iOS:</strong> Settings → Privacy → Tracking → disable for Flow XP</li>
              <li><strong>Android:</strong> Settings → Google → Privacy → Ads</li>
            </ul>
            <p>Subscribe to Premium for an ad-free experience.</p>
          </section>

          <section className="legal-section">
            <h2>8. Minors</h2>
            <p>
              Flow XP is not intended for children under 13 years old. If we discover that a minor has created an account,
              we will take steps to delete their data.
            </p>
          </section>

          <section className="legal-section">
            <h2>9. Changes to This Policy</h2>
            <p>
              We reserve the right to modify this privacy policy at any time. Changes will be notified via the app. Your
              continued use of the app after notification constitutes your acceptance of the new policy.
            </p>
          </section>

          <section className="legal-section contact-section">
            <h2>10. Contact</h2>
            <p>For any questions about this policy, contact us at:</p>
            <div className="contact-box">
              <p><strong>Email:</strong> <a href="mailto:flowledgeteam@gmail.com">flowledgeteam@gmail.com</a></p>
              <p><strong>Publisher:</strong> Flowledge</p>
              <p><strong>Application:</strong> Flow XP</p>
            </div>
          </section>

          <p className="closing-text">
            By using Flow XP, you acknowledge that you have read, understood, and accepted this Privacy Policy.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
